import { PointerEvent as ReactPointerEvent, RefObject, useCallback, useEffect, useRef, useState } from 'react';

export interface PanelPosition {
    x: number;
    y: number;
}

const STORAGE_KEY: string = 'chatPanelPosition';

/** Keep at least this much of the panel on screen, so it can always be grabbed again. */
const MIN_VISIBLE_PX: number = 80;

/** Where the panel sits the first time, before anyone has moved it. */
const DEFAULT_MARGIN: number = 24;

function readStoredPosition(): PanelPosition | null {
    try {
        const raw: string | null = window.localStorage.getItem(STORAGE_KEY);
        if (raw === null) {
            return null;
        }
        const parsed: PanelPosition = JSON.parse(raw);
        return Number.isFinite(parsed.x) && Number.isFinite(parsed.y) ? parsed : null;
    } catch {
        // A corrupt or unreadable value is not worth failing to open the panel over.
        return null;
    }
}

/**
 * Hold a position inside the viewport.
 *
 * Clamping matters more than it sounds: a panel dragged to the edge and then reopened on a
 * smaller screen would otherwise be parked off-screen with no way to reach it.
 */
function clamp(position: PanelPosition, panel: HTMLElement | null): PanelPosition {
    const width: number = panel?.offsetWidth ?? 420;
    const maxX: number = Math.max(0, window.innerWidth - MIN_VISIBLE_PX);
    const maxY: number = Math.max(0, window.innerHeight - MIN_VISIBLE_PX);

    return {
        x: Math.min(Math.max(position.x, MIN_VISIBLE_PX - width), maxX),
        y: Math.min(Math.max(position.y, 0), maxY),
    };
}

/**
 * Make a panel draggable by a handle, remembering where it was left.
 *
 * Written with pointer events rather than a drag library: it is one element with one handle, and
 * a dependency would be more code than this, not less. Pointer capture means the drag survives
 * the cursor leaving the handle, which is what makes a fast drag feel solid.
 */
export function useFloatingPanel(panel: RefObject<HTMLDivElement | null>, open: boolean) {
    const [position, setPosition] = useState<PanelPosition | null>(null);
    const [dragging, setDragging] = useState<boolean>(false);
    const grabOffset: RefObject<PanelPosition> = useRef<PanelPosition>({ x: 0, y: 0 });

    // Place it on first open: where it was left, or bottom right if it has never been moved.
    useEffect((): void => {
        if (!open || position !== null) {
            return;
        }
        const stored: PanelPosition | null = readStoredPosition();
        const width: number = panel.current?.offsetWidth ?? 420;
        const height: number = panel.current?.offsetHeight ?? 560;

        setPosition(clamp(stored ?? {
            x: window.innerWidth - width - DEFAULT_MARGIN,
            y: window.innerHeight - height - DEFAULT_MARGIN,
        }, panel.current));
    }, [open, position, panel]);

    // A window that shrinks must not leave the panel stranded outside it.
    useEffect((): (() => void) => {
        function onResize(): void {
            setPosition((current: PanelPosition | null): PanelPosition | null =>
                current === null ? null : clamp(current, panel.current));
        }
        window.addEventListener('resize', onResize);
        return (): void => window.removeEventListener('resize', onResize);
    }, [panel]);

    const onPointerMove = useCallback((event: PointerEvent): void => {
        setPosition(clamp({
            x: event.clientX - grabOffset.current.x,
            y: event.clientY - grabOffset.current.y,
        }, panel.current));
    }, [panel]);

    const onPointerUp = useCallback((): void => {
        setDragging(false);
        window.removeEventListener('pointermove', onPointerMove);
        window.removeEventListener('pointerup', onPointerUp);

        setPosition((current: PanelPosition | null): PanelPosition | null => {
            if (current !== null) {
                try {
                    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(current));
                } catch {
                    // Not being able to remember where it was left is not worth an error.
                }
            }
            return current;
        });
    }, [onPointerMove]);

    const startDrag = useCallback((event: ReactPointerEvent<HTMLElement>): void => {
        // Let the close button and anything else interactive in the header be clicked.
        if ((event.target as HTMLElement).closest('button') !== null) {
            return;
        }
        const bounds: DOMRect | undefined = panel.current?.getBoundingClientRect();
        if (bounds === undefined) {
            return;
        }
        grabOffset.current = { x: event.clientX - bounds.left, y: event.clientY - bounds.top };
        setDragging(true);
        window.addEventListener('pointermove', onPointerMove);
        window.addEventListener('pointerup', onPointerUp);
    }, [panel, onPointerMove, onPointerUp]);

    useEffect((): (() => void) => (): void => {
        window.removeEventListener('pointermove', onPointerMove);
        window.removeEventListener('pointerup', onPointerUp);
    }, [onPointerMove, onPointerUp]);

    return { position, dragging, startDrag };
}
